/** @jsx jsx */
import * as React from 'react';

import { jsx } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import { css } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import { TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { ReadOnlyReason } from '@atlassian/proforma-common-core/jira-common-models';
import type { FormValidationResult } from '@atlassian/proforma-common-core/jira-common-models';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics';
import messages from './messages';

interface RequestTypeProformaErrorProps {
    portalId: number;
    requestTypeId: number;
    admin: boolean;
    formValidationResult?: FormValidationResult;
}

type Props = RequestTypeProformaErrorProps & WithAnalyticsEventsProps & WrappedComponentProps;

const styles = css({
    marginTop: token('space.200', '16px'),
});

export class RequestTypeProformaError extends React.Component<Props> {
    fireRequestTypeViewedTrackEvent = () => {
        const { createAnalyticsEvent, requestTypeId, portalId, formValidationResult } = this.props;

        if (!createAnalyticsEvent) return;

        let analyticsReason: string | undefined;
        if (formValidationResult?.readOnlyMandatory.length) {
            analyticsReason = 'readOnlyMandatory';
        } else if (formValidationResult?.isConditionallyShown.length) {
            analyticsReason = 'isConditionallyShown';
        }
        const analyticsEvent = createAnalyticsEvent({
            portalId,
            requestTypeId,
            analyticsType: TRACK_EVENT_TYPE,
            action: Action.STOPPED,
            actionSubject: ActionSubject.FORM_VALIDATION,
            stopReason: analyticsReason,
        });
        analyticsEvent.fire();
    };

    componentDidMount() {
        this.fireRequestTypeViewedTrackEvent();
    }

    render() {
        const { admin, intl, formValidationResult } = this.props;

        let errorMessage;

        if (
            formValidationResult?.readOnlyMandatory.some(
                ({ readOnlyReason }) => readOnlyReason === ReadOnlyReason.Anonymous
            )
        ) {
            errorMessage = intl.formatMessage(messages.couldntLoadRequestAnonymousDescription);
        } else if (
            formValidationResult?.readOnlyMandatory.some(
                ({ readOnlyReason }) => readOnlyReason === ReadOnlyReason.Unlicensed
            )
        ) {
            errorMessage = intl.formatMessage(messages.couldntLoadRequestUnlicensedDescription);
        } else if (admin && formValidationResult?.isConditionallyShown.length) {
            errorMessage = intl.formatMessage(messages.couldntLoadRequestConditionalSectionDescription);
        } else {
            errorMessage = intl.formatMessage(messages.couldntLoadRequestGenericDescription);
        }

        return fg('migrate_request_intake_styled_components') ? (
            <div css={styles}>
                <SectionMessage appearance="error" title={intl.formatMessage(messages.couldntLoadRequestTitle)}>
                    {errorMessage}
                </SectionMessage>
            </div>
        ) : (
            <SectionMessageContainer>
                <SectionMessage appearance="error" title={intl.formatMessage(messages.couldntLoadRequestTitle)}>
                    {errorMessage}
                </SectionMessage>
            </SectionMessageContainer>
        );
    }
}
export default injectIntl(withAnalyticsEvents()(RequestTypeProformaError));

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const SectionMessageContainer = styled.div`
    margin-top: ${token('space.200', '16px')};
`;
